import { FC, memo, useMemo } from "react";
import {
    InvoiceDetails,
    MarketShipment,
    MarketShipmentPayment,
    Product,
} from "tm-shared/types";
import {
    formatDate,
    formatDateTimeSeconds,
    formatUAHNarrow,
} from "tm-shared/utils";

import { PrimaryText, SecondaryText } from "@/components/Typography/Styled";
import { useAppSelector } from "@/hooks/redux";

import {
    StyledAccordion,
    StyledAPHistoryPayments,
    StyledAPHistoryPaymentsRowBottom,
    StyledAPHistoryPaymentsRowBottomBlock,
    StyledAPHistoryPaymentsRowBottomLeft,
    StyledAPHistoryPaymentsRowBottomRight,
    StyledAPHistoryPaymentsRowWrapper,
    StyledAPHistoryProducts,
    StyledAPHistoryProductTableRow,
    StyledAPHistoryProductTableRowBlock,
    StyledAPHistoryProductTableRowWrapper,
    StyledHeader,
    StyledHeaderBlock,
    StyledHistoryContainer,
    StyledPrice,
} from "./Styled";

interface AccordionHeaderProps {
    primary: string;
    secondary?: string;
    price?: string;
    orderId?: string;
    date?: string;
    sku?: string;
}

interface AccordionItem {
    key: string;
    label: React.ReactNode;
    nestedItems?: AccordionItem[];
    children?: React.ReactNode;
}

interface AccordionLevelProps {
    items: AccordionItem[];
    level?: number;
}

interface ShipmentAccordionsProps {
    shipments: MarketShipment[];
    invoices: InvoiceDetails[];
    payments: Record<string, MarketShipmentPayment[]>;
}

const AccordionHeader: FC<AccordionHeaderProps> = memo(
    ({ primary, secondary, price, orderId, date, sku }) => (
        <>
            <StyledHeader>
                <StyledHeaderBlock>
                    <PrimaryText>{primary}</PrimaryText>

                    {secondary && <SecondaryText>{secondary}</SecondaryText>}
                </StyledHeaderBlock>
                {(date || price || sku) && (
                    <StyledHeaderBlock>
                        {date && (
                            <SecondaryText style={{ whiteSpace: "nowrap" }}>
                                {date}
                            </SecondaryText>
                        )}

                        {price && <StyledPrice>{price}</StyledPrice>}

                        {sku && (
                            <SecondaryText style={{ textAlign: "right" }}>
                                {sku}
                            </SecondaryText>
                        )}
                    </StyledHeaderBlock>
                )}
            </StyledHeader>

            {orderId && (
                <PrimaryText style={{ marginTop: "4px" }}>
                    {orderId}
                </PrimaryText>
            )}
        </>
    ),
);

AccordionHeader.displayName = "AccordionHeader";

const AccordionLevel: FC<AccordionLevelProps> = memo(({ items, level = 1 }) => (
    <StyledAccordion
        accordion
        items={items.map(({ nestedItems, ...item }) => ({
            ...item,
            children:
                item.children ||
                (nestedItems && (
                    <AccordionLevel items={nestedItems} level={level + 1} />
                )),
        }))}
        level={level}
    />
));

AccordionLevel.displayName = "AccordionLevel";

const calculateProductsSum = (products: Array<{ sum: number }>): number =>
    products.reduce((acc, { sum }) => acc + sum, 0);

const getInvoiceSum = (invoice: InvoiceDetails): number =>
    Object.values(invoice.products).reduce(
        (acc, { count, price }) => acc + Math.round(count * price),
        0,
    );

const createAccordionData = (
    shipments: MarketShipment[],
    productsList: Record<string, Product>,
    invoices: InvoiceDetails[],
    payments: Record<string, MarketShipmentPayment[]>,
): AccordionItem[] =>
    shipments.map((shipment) => {
        const { products = [] } = shipment.payload || {};
        const totalSum = calculateProductsSum(products);
        const productCount = products.length;
        const productItems = products.map(({ barcode, amount, sum }, index) => (
            <StyledAPHistoryProductTableRowWrapper key={`${barcode}-${index}`}>
                <StyledAPHistoryProductTableRow>
                    <PrimaryText>
                        {productsList[barcode]?.name || "Невідомий товар"}
                    </PrimaryText>

                    <StyledAPHistoryProductTableRowBlock>
                        <SecondaryText style={{ textAlign: "right" }}>
                            {amount}
                        </SecondaryText>
                        <StyledPrice>{formatUAHNarrow(sum)}</StyledPrice>
                    </StyledAPHistoryProductTableRowBlock>
                </StyledAPHistoryProductTableRow>
            </StyledAPHistoryProductTableRowWrapper>
        ));
        const invoice = invoices?.find(({ id }) => id === shipment.invoiceId);
        const invoiceSum = invoice ? getInvoiceSum(invoice) : 0;
        const nestedItems: AccordionItem[] = [
            {
                key: shipment.consignmentId,
                label: (
                    <AccordionHeader
                        primary="Доставлені товари"
                        sku={`${productCount} SKU`}
                    />
                ),
                children: (
                    <StyledAPHistoryProducts>
                        {productItems}
                    </StyledAPHistoryProducts>
                ),
            },
        ];

        if (invoice) {
            const invoiceItems = Object.values(invoice.products).map(
                ({ barcode, name, count, price }, index) => (
                    <StyledAPHistoryProductTableRowWrapper
                        key={`${barcode}-${index}`}
                    >
                        <StyledAPHistoryProductTableRow>
                            <PrimaryText>
                                {name || "Невідомий товар"}
                            </PrimaryText>
                            <StyledAPHistoryProductTableRowBlock>
                                <SecondaryText style={{ textAlign: "right" }}>
                                    {count}
                                </SecondaryText>
                                <StyledPrice>
                                    {formatUAHNarrow(Math.round(count * price))}
                                </StyledPrice>
                            </StyledAPHistoryProductTableRowBlock>
                        </StyledAPHistoryProductTableRow>
                    </StyledAPHistoryProductTableRowWrapper>
                ),
            );

            nestedItems.push({
                key: `${shipment.consignmentId}-invoice`,
                label: (
                    <AccordionHeader
                        primary="Замовлення"
                        orderId={invoice.id}
                        date={formatDate(invoice.creationDate)}
                    />
                ),
                nestedItems: [
                    {
                        key: `${shipment.consignmentId}-invoice-details`,
                        label: (
                            <AccordionHeader
                                primary="Деталі замовлення"
                                price={formatUAHNarrow(invoiceSum)}
                            />
                        ),
                        children: (
                            <StyledAPHistoryProducts>
                                {invoiceItems}
                            </StyledAPHistoryProducts>
                        ),
                    },
                ],
            });
        }
        const shipmentPayments = payments
            ? payments[shipment.consignmentId]
            : [];
        if (shipmentPayments?.length) {
            const paymentsSum = calculateProductsSum(
                shipmentPayments.map((p) => ({ sum: p.amount })),
            );
            let debtRemaining = totalSum;
            let debtCurrent = totalSum;

            const paymentItems = shipmentPayments.map(
                ({ debtId, paymentDate, amount }, index) => {
                    if (index !== 0) debtCurrent = debtRemaining;
                    debtRemaining -= amount;
                    return (
                        <StyledAPHistoryPaymentsRowWrapper
                            key={`${debtId}-${index}`}
                        >
                            <SecondaryText>
                                {formatDateTimeSeconds(paymentDate)}
                            </SecondaryText>
                            <StyledAPHistoryPaymentsRowBottom>
                                <StyledAPHistoryPaymentsRowBottomLeft>
                                    <SecondaryText>Сума</SecondaryText>
                                    <StyledPrice>
                                        {formatUAHNarrow(amount)}
                                    </StyledPrice>
                                </StyledAPHistoryPaymentsRowBottomLeft>
                                <StyledAPHistoryPaymentsRowBottomRight>
                                    <StyledAPHistoryPaymentsRowBottomBlock>
                                        <SecondaryText fontSize={12}>
                                            Борг:
                                        </SecondaryText>
                                        <SecondaryText fontSize={12}>
                                            Залишок:
                                        </SecondaryText>
                                    </StyledAPHistoryPaymentsRowBottomBlock>
                                    <StyledAPHistoryPaymentsRowBottomBlock>
                                        <StyledPrice>
                                            {formatUAHNarrow(debtCurrent)}
                                        </StyledPrice>
                                        <StyledPrice>
                                            {formatUAHNarrow(debtRemaining)}
                                        </StyledPrice>
                                    </StyledAPHistoryPaymentsRowBottomBlock>
                                </StyledAPHistoryPaymentsRowBottomRight>
                            </StyledAPHistoryPaymentsRowBottom>
                        </StyledAPHistoryPaymentsRowWrapper>
                    );
                },
            );

            nestedItems.push({
                key: `${shipment.consignmentId}-payments`,
                label: (
                    <AccordionHeader
                        primary="Оплати"
                        price={formatUAHNarrow(paymentsSum)}
                    />
                ),
                children: (
                    <StyledAPHistoryPayments>
                        {paymentItems}
                    </StyledAPHistoryPayments>
                ),
            });
        }
        return {
            key: `${shipment.consignmentId}-root`,
            label: (
                <AccordionHeader
                    primary={`${shipment.consignmentId}`}
                    secondary={
                        invoice?.paymentForm
                            ? `Форма ${invoice.paymentForm}`
                            : ""
                    }
                    date={formatDate(shipment.shipmentDate)}
                    price={formatUAHNarrow(totalSum)}
                />
            ),
            nestedItems,
        };
    });

export const ShipmentAccordions: FC<ShipmentAccordionsProps> = memo(
    ({ shipments, invoices, payments }) => {
        const { products } = useAppSelector((state) => state.offline);

        const accordionItems = useMemo(
            () => createAccordionData(shipments, products, invoices, payments),
            [shipments, products, invoices, payments],
        );

        return (
            <StyledHistoryContainer>
                {shipments && shipments.length > 0 ? (
                    <AccordionLevel items={accordionItems} />
                ) : (
                    <PrimaryText
                        fontSize={16}
                        style={{ textAlign: "center", paddingBottom: "16px" }}
                    >
                        За поданий період історія продажів не знайдена
                    </PrimaryText>
                )}
            </StyledHistoryContainer>
        );
    },
);

ShipmentAccordions.displayName = "ShipmentAccordions";
